import styled, { css } from "styled-components";
import { popoverMotion } from "../../../design-system/tokens/popoverMotion";
import { rawColors } from "../../../design-system/tokens/colors";
import { spacing } from "../../../design-system/tokens/spacing";
import { borderRadius } from "../../../design-system/tokens/borders";
import { shadows } from "../../../design-system/tokens/shadows";
import { fontSizes, letterSpacing, lineHeights, typographyMixin } from "../../../design-system/tokens/typography";
import type { ActionMenuDensity } from "./ActionMenu.types";
import { hideScrollbar } from "../Dropdown/Dropdown.styles";

export const Wrap = styled.div`
  display: inline-flex;
  position: relative;
`;


export const Menu = styled.ul<{ $showScrollbar?: boolean; $density?: ActionMenuDensity; $panel?: boolean; $closing?: boolean }>`
  position: fixed;
  ${({ $closing }) => popoverMotion($closing)}
  margin: 0;
  list-style: none;
  padding: ${spacing[2]};
  background-color: ${rawColors.structural.white};
  border: 1px solid ${rawColors.stroke[200]};
  border-radius: ${borderRadius.xl};
  box-shadow: ${shadows.component.dropdown};
  z-index: 2000;
  overflow-y: auto;
  box-sizing: border-box;

  ${({ $showScrollbar }) => !$showScrollbar && hideScrollbar}

  ${({ $density }) =>
    $density === "compact" &&
    css`
      padding: ${spacing[1.5]};
    `}

  ${({ $density }) =>
    $density === "tiled" &&
    css`
      display: flex;
      flex-direction: column;
      gap: ${spacing[0.5]};
      padding: ${spacing[1.5]};
      box-shadow: ${shadows.component.overlayMenu};
    `}

  ${({ $panel }) =>
    $panel &&
    css`
      border-radius: ${borderRadius[20]};
      box-shadow: ${shadows.component.overlayMenu};
    `}
`;

/* GAP: roomy row padding has no spacing token */
const ROOMY_ROW_PADDING = "0.5625rem 0.75rem";

export const MenuItemButton = styled.button<{
  $danger?: boolean;
  $highlight?: boolean;
  $roomy?: boolean;
  $fillFocus?: boolean;
  $density?: ActionMenuDensity;
  $currentTint?: string;
  $panel?: boolean;
}>`
  display: flex;
  align-items: center;
  width: 100%;
  gap: ${spacing[2]};
  padding: ${({ $roomy }) => ($roomy ? ROOMY_ROW_PADDING : `${spacing[2]} ${spacing[3]}`)};
  background: ${({ $highlight }) => ($highlight ? rawColors.structural[100] : "none")};
  border: none;
  border-radius: ${borderRadius.sm};
  ${typographyMixin("body", 3)}
  color: ${({ $danger }) =>
    $danger ? rawColors.accent.red500 : rawColors.text.highEmphasis};
  cursor: pointer;
  text-align: left;

  ${({ $density }) =>
    $density === "compact" &&
    css`
      /* GAP: design 32px rows and 13px type have no tokens. */
      min-height: 2rem;
      padding: 0 ${spacing[2.5]};
      border-radius: ${borderRadius.lg};
      font-size: 0.8125rem;
      line-height: 1.25rem;
      /* One line per item: a longer label widens the menu (minMenuWidth) instead of wrapping. */
      white-space: nowrap;
    `}

  ${({ $density }) =>
    $density === "tiled" &&
    css`
      gap: ${spacing[2.5]};
      padding: ${spacing[2]};
      border-radius: ${borderRadius.lg};
    `}

  ${({ $currentTint }) =>
    $currentTint &&
    css`
      background: ${$currentTint};
    `}

  &:hover:not(:disabled) {
    background: ${({ $currentTint }) => $currentTint ?? rawColors.grey[50]};
  }

  &:disabled,
  &[aria-disabled="true"] {
    cursor: not-allowed;
    color: ${rawColors.text.lowEmphasis};
  }

  ${({ $panel }) =>
    $panel &&
    css`
      min-height: 2.5rem;
      padding: 0 ${spacing[2.5]};
      gap: ${spacing[2.5]};
      border-radius: ${borderRadius.xl};
      &[aria-current="true"] {
        min-height: 2.75rem;
        background: ${rawColors.primary[100]};
        font-weight: 600;
      }
    `}
  ${({ $fillFocus }) =>
    $fillFocus &&
    css`
      &:focus-visible:not(:disabled) {
        outline: none;
        background: ${rawColors.primary[100]};
      }
    `}

  /* Soon rows stay focusable; their tag shows on hover or keyboard focus only. */
  &[aria-disabled="true"]:hover,
  &[aria-disabled="true"]:focus-visible {
    background: ${rawColors.grey[50]};
  }
`;

/** A small caps group label above a run of rows (panel variant). */
export const SectionLabel = styled.li`
  list-style: none;
  padding: ${spacing[2]} ${spacing[3]} ${spacing[1]};
  ${typographyMixin("caption", 1)}
  font-size: ${fontSizes["11"]};
  line-height: ${lineHeights["14"]};
  letter-spacing: ${letterSpacing.overline};
  text-transform: uppercase;
  color: ${rawColors.text.lowEmphasis};
`;

export const Footer = styled.li`
  list-style: none;
  margin: ${spacing[2]} ${spacing[1.5]} ${spacing[1]};
  padding-top: ${spacing[2.5]};
  border-top: 1px solid ${rawColors.stroke[200]};
  display: flex;
  flex-wrap: wrap;
  gap: ${spacing[1.5]};
`;

/** The Soon badge's slot at the row end: shown on hover or keyboard focus, or always with `$always`. */
export const SoonSlot = styled.span<{ $always?: boolean }>`
  display: inline-flex;
  margin-left: auto;
  opacity: ${({ $always }) => ($always ? 1 : 0)};

  ${MenuItemButton}:hover &,
  ${MenuItemButton}:focus-visible & {
    opacity: 1;
  }
`;

/** Opt-in `endHint`: small secondary text pushed to the end of the row. */
export const ItemHint = styled.span`
  margin-left: auto;
  padding-left: ${spacing[2]};
  flex-shrink: 0;
  ${typographyMixin("caption", 3)}
  color: ${rawColors.text.mediumEmphasis};
`;

/** 28px rounded icon tile: white with the accent icon on the current row, grey with a muted icon otherwise. */
export const ItemIconTile = styled.span<{ $current?: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  /* GAP: design 28px tile has no size token. */
  width: 1.75rem;
  height: 1.75rem;
  flex-shrink: 0;
  border-radius: ${borderRadius.lg};
  background: ${({ $current }) => ($current ? rawColors.structural.white : rawColors.grey[100])};
  color: ${({ $current }) => ($current ? rawColors.secondary[700] : rawColors.text.lowEmphasis)};
`;

/** The row's trailing tag: never shrinks, so a tag such as "Sample" is not cut to "Sam…". */
export const ItemTrailing = styled.span`
  display: inline-flex;
  flex-shrink: 0;
  margin-left: auto;
  white-space: nowrap;
`;

export const ItemContent = styled.span`
  display: flex;
  flex-direction: column;
  gap: ${spacing[0.5]};
  min-width: 0;
  flex: 1 1 auto;
`;

export const ItemLabel = styled.span`
  ${typographyMixin("body", 3)}
`;

export const ItemShortcut = styled.span`
  margin-left: auto;
  padding-left: ${spacing[3]};
  ${typographyMixin("caption", 4)}
  color: ${rawColors.text.lowEmphasis};
`;

export const ItemDescription = styled.span`
  white-space: normal;
  ${typographyMixin("caption", 4)}
  color: ${rawColors.text.mediumEmphasis};
`;

export const ItemIcon = styled.span<{ $danger?: boolean; $color?: string }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  flex-shrink: 0;
  color: ${({ $danger, $color }) =>
    $danger
      ? rawColors.accent.red500
      : ($color ?? rawColors.text.interactive)};
`;

export const Divider = styled.li`
  list-style: none;
  height: 1px;
  margin: ${spacing[1]} 0;
  background: ${rawColors.stroke[200]};
`;
